import React from 'react';
import { parseTikTokMessage } from '../lib/tiktokEmoji';

// Events can arrive without a full user object; never crash the feed over it
export const displayName = (user) => user?.nickname || user?.username || 'Someone';

// Text a viewer wrote: subscriber emote images in place, and TikTok's [code] emoji drawn as emoji
// (unknown codes stay as text)
const renderViewerText = (text, emotes) => parseTikTokMessage(text, emotes).map((piece, i) => {
    if (piece.type === 'subEmote') {
        return <img key={i} src={piece.image} alt="emote" className="tiktok-sub-emote" />;
    }
    if (piece.type === 'emoji') {
        return <span key={i} className="tiktok-emoji" title={`[${piece.code}]`}>{piece.emoji}</span>;
    }
    return piece.text;
});

// One entry in a TikTok chat feed. Purely presentational.
const TikTokFeedItem = ({ msg }) => {
    switch (msg.type) {
        case 'chat':
            return (
                <div className="tiktok-message chat-message">
                    {msg.user?.profilePicture && (
                        <img
                            src={msg.user.profilePicture}
                            alt={displayName(msg.user)}
                            className="user-avatar"
                        />
                    )}
                    <div className="message-content">
                        <div className="user-info">
                            <span className="username">{displayName(msg.user)}</span>
                            {msg.user?.badge && (
                                <span className="user-badge">{msg.user.badge}</span>
                            )}
                        </div>
                        <div className="message-text">{renderViewerText(msg.message, msg.emotes)}</div>
                    </div>
                </div>
            );

        case 'gift':
            return (
                <div className="tiktok-message gift-message">
                    {msg.gift?.image && (
                        <img
                            src={msg.gift.image}
                            alt={msg.gift.name}
                            className="gift-image"
                        />
                    )}
                    <div className="message-content">
                        <span className="gift-sender">{displayName(msg.user)}</span>
                        <span className="gift-text">
                            {' sent '}
                            <strong>{msg.gift?.name || 'a gift'}</strong>
                            {msg.gift?.repeatCount > 1 && ` x${msg.gift.repeatCount}`}
                        </span>
                    </div>
                </div>
            );

        case 'follow':
            return (
                <div className="tiktok-message event-message">
                    <span className="event-icon">❤️</span>
                    <span className="event-text">
                        <strong>{displayName(msg.user)}</strong> followed!
                    </span>
                </div>
            );

        case 'share':
            return (
                <div className="tiktok-message event-message">
                    <span className="event-icon">🔗</span>
                    <span className="event-text">
                        <strong>{displayName(msg.user)}</strong> shared the stream
                    </span>
                </div>
            );

        case 'member':
            return (
                <div className="tiktok-message event-message">
                    <span className="event-icon">👋</span>
                    <span className="event-text">
                        <strong>{displayName(msg.user)}</strong> joined!
                    </span>
                </div>
            );

        case 'like':
            return (
                <div className="tiktok-message event-message">
                    <span className="event-icon">👍</span>
                    <span className="event-text">
                        <strong>{displayName(msg.user)}</strong> sent {msg.likeCount} likes
                        {msg.totalLikeCount > 0 && ` (${msg.totalLikeCount.toLocaleString()} total)`}
                    </span>
                </div>
            );

        case 'emote':
            return (
                <div className="tiktok-message event-message">
                    {msg.emote?.image && (
                        <img
                            src={msg.emote.image}
                            alt="emote"
                            className="emote-image"
                            style={{ width: '24px', height: '24px', marginRight: '8px' }}
                        />
                    )}
                    <span className="event-text">
                        <strong>{displayName(msg.user)}</strong> sent a sticker
                    </span>
                </div>
            );

        case 'envelope':
            return (
                <div className="tiktok-message event-message">
                    <span className="event-icon">💰</span>
                    <span className="event-text">
                        Treasure chest received! {msg.coins} coins
                    </span>
                </div>
            );

        case 'question':
            return (
                <div className="tiktok-message chat-message">
                    <span className="event-icon">❓</span>
                    <div className="message-content">
                        <div className="user-info">
                            <span className="username">{displayName(msg.user)}</span>
                        </div>
                        <div className="message-text">
                            <strong>Q:</strong> {renderViewerText(msg.questionText)}
                        </div>
                    </div>
                </div>
            );

        case 'linkMicBattle':
            return (
                <div className="tiktok-message event-message">
                    <span className="event-icon">⚔️</span>
                    <span className="event-text">
                        Battle started:{' '}
                        {msg.battleUsers?.map((user, idx) => (
                            <span key={user.id}>
                                {idx > 0 && ' VS '}
                                <strong>{user.nickname || user.username}</strong>
                            </span>
                        ))}
                    </span>
                </div>
            );

        case 'linkMicArmies':
            return (
                <div className="tiktok-message event-message">
                    <span className="event-icon">🎖️</span>
                    <span className="event-text">
                        Battle update received
                    </span>
                </div>
            );

        case 'liveIntro':
            return (
                <div className="tiktok-message system-message">
                    📺 Live intro message
                </div>
            );

        case 'superFan':
            return (
                <div className="tiktok-message event-message">
                    <span className="event-icon">⭐</span>
                    <span className="event-text">
                        <strong>{displayName(msg.user)}</strong> became a Super Fan!
                    </span>
                </div>
            );

        case 'system':
            return (
                <div className="tiktok-message system-message">
                    {msg.message}
                </div>
            );

        default:
            return null;
    }
};

export default TikTokFeedItem;
